import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { cn } from '@/lib/utils';
import { Calculator, UserCog, AlertCircle, Droplets, Dumbbell, Target } from 'lucide-react';
import { AntropometriaCard } from './AntropometriaCard';
import { pesoIdeal } from '@/lib/antropometria';

const ACTIVIDAD = {
  sedentario: 1.2,
  ligero:     1.375,
  moderado:   1.55,
  intenso:    1.725,
};

const ACTIVIDAD_LABEL = {
  sedentario: 'Sedentario',
  ligero:     'Ligero',
  moderado:   'Moderado',
  intenso:    'Intenso',
};

const OBJETIVO_MACRO = {
  bajar_peso:          { p: 0.30, c: 0.40, g: 0.30 },
  subir_peso:          { p: 0.25, c: 0.50, g: 0.25 },
  control_enfermedad:  { p: 0.25, c: 0.45, g: 0.30 },
  mantenimiento:       { p: 0.20, c: 0.50, g: 0.30 },
  otro:                { p: 0.20, c: 0.50, g: 0.30 },
};

const IMC_INFO = (imc) => {
  if (imc < 18.5) return { label: 'Bajo peso',          color: 'text-blue-500',    bg: 'bg-blue-50 dark:bg-blue-900/20',    border: 'border-blue-200 dark:border-blue-800' };
  if (imc < 25)   return { label: 'Peso normal',         color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800' };
  if (imc < 30)   return { label: 'Sobrepeso',           color: 'text-amber-600',   bg: 'bg-amber-50 dark:bg-amber-900/20',  border: 'border-amber-200 dark:border-amber-800' };
  if (imc < 35)   return { label: 'Obesidad grado I',    color: 'text-orange-600',  bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800' };
  if (imc < 40)   return { label: 'Obesidad grado II',   color: 'text-rose-600',    bg: 'bg-rose-50 dark:bg-rose-900/20',    border: 'border-rose-200 dark:border-rose-800' };
  return                  { label: 'Obesidad grado III', color: 'text-red-700',     bg: 'bg-red-50 dark:bg-red-900/20',      border: 'border-red-200 dark:border-red-800' };
};

const MISSING_LABEL = {
  peso:                  'Peso (kg)',
  estatura:              'Estatura (cm)',
  sexo:                  'Sexo biológico',
  'fecha de nacimiento': 'Fecha de nacimiento',
};

const StatBox = ({ label, value, unit, sub, color, icon: Icon }) => (
  <div className="rounded-lg border border-border/60 p-3 space-y-0.5">
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className="h-3 w-3 text-muted-foreground" />}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
    <p className={cn('text-2xl font-bold', color)}>
      {value ?? '—'}
      {value && unit && <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span>}
    </p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const MacroBar = ({ color, gram, kcal, label, perKg }) => (
  <div className="space-y-1.5">
    <div className={cn('h-1.5 rounded-full', color)} />
    <p className="text-2xl font-bold">{gram}g</p>
    <p className="text-xs text-muted-foreground font-medium">{label}</p>
    <p className="text-xs text-muted-foreground">{kcal} kcal</p>
    {perKg && <p className="text-xs font-medium text-foreground">{perKg} g/kg</p>}
  </div>
);

export const CalcNutricional = ({ patient, onEditarPerfil }) => {
  const [sexoLocal, setSexoLocal] = useState(patient.sexo || '');

  const edad = useMemo(() => {
    if (!patient.fecha_nacimiento) return null;
    const hoy = new Date();
    const nac = new Date(patient.fecha_nacimiento);
    let e = hoy.getFullYear() - nac.getFullYear();
    if (hoy < new Date(hoy.getFullYear(), nac.getMonth(), nac.getDate())) e--;
    return e;
  }, [patient.fecha_nacimiento]);

  const calc = useMemo(() => {
    const { peso, estatura, actividad_fisica, objetivo } = patient;
    if (!peso || !estatura || !sexoLocal || !edad) return null;

    const p = parseFloat(peso);
    const e = parseFloat(estatura);

    const tmb = sexoLocal === 'masculino'
      ? 10 * p + 6.25 * e - 5 * edad + 5
      : 10 * p + 6.25 * e - 5 * edad - 161;

    const factor  = ACTIVIDAD[actividad_fisica] ?? 1.375;
    const get     = Math.round(tmb * factor);
    const macros  = OBJETIVO_MACRO[objetivo] ?? OBJETIVO_MACRO.mantenimiento;
    const imc     = p / Math.pow(e / 100, 2);
    const protsG  = Math.round((get * macros.p) / 4);

    // Agua: 35 ml por kg de peso
    const agua = Math.round(35 * p);

    // Proteína por kg
    const proteinaPerKg = (protsG / p).toFixed(1);

    // Progreso hacia peso ideal
    const pi = pesoIdeal(e, sexoLocal);
    let progreso = null;
    if (pi) {
      const idealNum = parseFloat(pi.ideal);
      const maxNum   = parseFloat(pi.maximo);
      const diff     = p - idealNum;
      const encima   = diff > 0;
      const sobreMax = p > maxNum;

      // Porcentaje del bar: cuánto falta para llegar al ideal desde el peso actual
      // Si peso > ideal: bar va de ideal a peso actual (cuánto sobra)
      // Usamos el rango ideal→ideal×1.5 como referencia del bar (100%)
      const rangoRef = idealNum * 0.5;
      const barPct   = encima ? Math.min(100, (diff / rangoRef) * 100) : 0;

      let mensaje, msgColor;
      if (!encima) {
        mensaje  = 'En o por debajo del peso ideal';
        msgColor = 'text-emerald-600';
      } else if (!sobreMax) {
        mensaje  = `${diff.toFixed(1)} kg sobre el ideal · dentro del rango saludable`;
        msgColor = 'text-amber-600';
      } else {
        mensaje  = `${(p - maxNum).toFixed(1)} kg sobre el máximo recomendado`;
        msgColor = 'text-rose-600';
      }

      progreso = { ideal: pi.ideal, maximo: pi.maximo, barPct, mensaje, msgColor, encima, sobreMax };
    }

    return {
      tmb:             Math.round(tmb),
      get,
      imc:             imc.toFixed(1),
      imcInfo:         IMC_INFO(imc),
      proteinas:       protsG,
      carbos:          Math.round((get * macros.c) / 4),
      grasas:          Math.round((get * macros.g) / 9),
      protCalc:        Math.round(get * macros.p),
      carbCalc:        Math.round(get * macros.c),
      grasCalc:        Math.round(get * macros.g),
      factorLabel:     ACTIVIDAD_LABEL[actividad_fisica] ?? 'Ligero',
      agua,
      proteinaPerKg,
      progreso,
    };
  }, [patient, sexoLocal, edad]);

  const missing = [];
  if (!patient.peso)             missing.push('peso');
  if (!patient.estatura)         missing.push('estatura');
  if (!sexoLocal)                missing.push('sexo');
  if (!patient.fecha_nacimiento) missing.push('fecha de nacimiento');

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calculator className="h-4 w-4 text-emerald-500" />
            Calculadora nutricional
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Selector de sexo si no está en el perfil */}
          {!patient.sexo && (
            <div className="space-y-1">
              <Label>
                Sexo biológico{' '}
                <span className="text-muted-foreground text-xs">(para el cálculo)</span>
              </Label>
              <div className="flex gap-2">
                {['masculino', 'femenino'].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSexoLocal(s)}
                    className={cn(
                      'flex-1 py-2 rounded-lg border text-sm capitalize transition-colors cursor-pointer',
                      sexoLocal === s
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-border hover:border-emerald-400'
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {missing.length > 0 && !calc ? (
            <div className="rounded-xl border border-dashed border-border/70 bg-muted/30 p-6 text-center space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-3">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Faltan datos del paciente</p>
                <p className="text-xs text-muted-foreground">Para calcular necesito:</p>
                <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                  {missing.map(m => (
                    <span key={m} className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-xs font-medium px-2.5 py-1">
                      {MISSING_LABEL[m]}
                    </span>
                  ))}
                </div>
              </div>
              {onEditarPerfil && (
                <button
                  type="button"
                  onClick={onEditarPerfil}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 transition-colors cursor-pointer"
                >
                  <UserCog className="h-4 w-4" />
                  Completar perfil del paciente
                </button>
              )}
            </div>
          ) : calc && (
            <>
              {/* Fila 1: TMB · GET · IMC */}
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="TMB" value={calc.tmb} unit="kcal/día" sub="Metabolismo basal" />
                <StatBox label="GET" value={calc.get} unit="kcal/día" sub={`Actividad: ${calc.factorLabel}`} color="text-emerald-600" />
                <div className={cn('rounded-lg border p-3 space-y-0.5', calc.imcInfo.bg, calc.imcInfo.border)}>
                  <p className="text-xs text-muted-foreground">IMC</p>
                  <p className={cn('text-2xl font-bold', calc.imcInfo.color)}>{calc.imc}</p>
                  <p className={cn('text-xs font-semibold', calc.imcInfo.color)}>{calc.imcInfo.label}</p>
                </div>
              </div>

              {/* Fila 2: Agua · Proteína/kg */}
              <div className="grid grid-cols-2 gap-3">
                <StatBox
                  label="Agua diaria recomendada"
                  value={calc.agua}
                  unit="ml/día"
                  sub={`${(calc.agua / 1000).toFixed(1)} L · 35 ml × ${patient.peso} kg`}
                  color="text-sky-500"
                  icon={Droplets}
                />
                <StatBox
                  label="Proteína por kg"
                  value={calc.proteinaPerKg}
                  unit="g/kg"
                  sub={calc.proteinaPerKg < 0.8 ? 'Bajo el mínimo recomendado' : calc.proteinaPerKg <= 2.0 ? 'Dentro del rango óptimo' : 'Por encima del rango típico'}
                  color={calc.proteinaPerKg < 0.8 ? 'text-rose-500' : calc.proteinaPerKg <= 2.0 ? 'text-emerald-600' : 'text-amber-500'}
                  icon={Dumbbell}
                />
              </div>

              {/* Fila 3: Macros */}
              <div className="rounded-lg border border-border/60 p-4 space-y-3">
                <p className="text-sm font-medium">
                  Distribución de macros
                  {patient.objetivo && (
                    <span className="text-muted-foreground font-normal">
                      {' '}— {patient.objetivo.replace(/_/g, ' ')}
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <MacroBar color="bg-blue-400"  gram={calc.proteinas} kcal={calc.protCalc} label="Proteína"      perKg={calc.proteinaPerKg} />
                  <MacroBar color="bg-amber-400" gram={calc.carbos}    kcal={calc.carbCalc} label="Carbohidratos" />
                  <MacroBar color="bg-rose-400"  gram={calc.grasas}    kcal={calc.grasCalc} label="Grasas" />
                </div>
              </div>

              {/* Fila 4: Progreso hacia peso ideal */}
              {calc.progreso && (
                <div className="rounded-lg border border-border/60 p-4 space-y-3">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    Progreso hacia peso ideal
                  </p>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Ideal: <strong className="text-foreground">{calc.progreso.ideal} kg</strong></span>
                    <span>Actual: <strong className="text-foreground">{patient.peso} kg</strong></span>
                    <span>Máx: <strong className="text-foreground">{calc.progreso.maximo} kg</strong></span>
                  </div>
                  <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        calc.progreso.sobreMax  ? 'bg-rose-500' :
                        calc.progreso.encima    ? 'bg-amber-400' : 'bg-emerald-500'
                      )}
                      style={{ width: calc.progreso.encima ? `${calc.progreso.barPct}%` : '100%' }}
                    />
                  </div>
                  <p className={cn('text-xs font-medium', calc.progreso.msgColor)}>
                    {calc.progreso.mensaje}
                  </p>
                </div>
              )}

              {/* Datos usados */}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>Peso: <span className="font-medium text-foreground">{patient.peso} kg</span></span>
                <span>Estatura: <span className="font-medium text-foreground">{patient.estatura} cm</span></span>
                <span>Edad: <span className="font-medium text-foreground">{edad} años</span></span>
                <span>Actividad: <span className="font-medium text-foreground">{calc.factorLabel}</span></span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AntropometriaCard patient={patient} sexo={sexoLocal} edad={edad} />
    </div>
  );
};
